/* Copyright (c) 2020-present CloudIO Inc */

import type { ItemID } from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import { useStateXSelector } from '@cloudio-saas/statex';
import { Divider, Menu, MenuItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { columnsHideShowSelector } from 'components/DataGrid/GridState';
import Icon from 'icons/Icon';
import React, { useRef } from 'react';
import { useTranslate } from '../../utils/lang';
import { useStaticRuntime } from '../../common/Context';
import useHandleClickOutside from '../../hooks/useHandleClickOutside';
import { stopPropagation } from '../../utils/events';
import zIndex from 'constants/ZIndex';
// import { useColumnsRuntimeData } from 'components/DataGrid/useColumns';

const useStyles = makeStyles(() => ({
  menuItem: {
    minWidth: 180,
  },
  icon: {
    marginRight: 12,
  },
  iconRight: {
    marginLeft: 12,
  },
}));

export interface MenuProps {
  appUid: string;
  pageId: ItemID;
  gridItemId: ItemID;
  platform: Platform;
}

export default function ColumnsSubMenu({
  platform,
  gridItemId,
  pageId,
  appUid,
}: MenuProps): React.ReactElement {
  const classes = useStyles();
  let { t } = useTranslate();
  const staticRuntime = useStaticRuntime();
  const menuRef = useRef<HTMLUListElement>(null);
  if (staticRuntime) {
    t = (key) => key ?? '';
  }
  const [menuEl, setMenuEl] = React.useState<HTMLElement | undefined>(
    undefined,
  );
  const cols = useStateXSelector(columnsHideShowSelector, {
    params: { appUid, pageId, gridItemId },
  });

  function handleClose() {
    setMenuEl(undefined);
  }

  const hasHiddenCols = !!cols.find((c) => !c.visible);

  // const { updateRuntimeProps } = useColumnsRuntimeData(pageId, gridItemId);
  const totalVisible = cols.filter((c) => c.visible).length;
  const disableHideAll = totalVisible <= 1;
  useHandleClickOutside(menuRef, handleClose, !!menuEl);

  return (
    <MenuItem
      data-testid="grid-columns"
      onClick={(event) => {
        setMenuEl(event.currentTarget);
      }}
    >
      <Icon className={classes.icon} iconName="columns" />
      <span style={{ flex: 1 }}>{t('Columns')}</span>
      <Icon
        data-testid="open-columns-menu"
        onClick={(event) => {
          setMenuEl(event.currentTarget);
        }}
        onMouseEnter={(event) => {
          setMenuEl(event.currentTarget);
        }}
        className={classes.iconRight}
        iconName="chevron-right"
      />
      <Menu
        anchorEl={menuEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={!!menuEl}
        onClose={handleClose}
        MenuListProps={{
          onMouseLeave: handleClose,
          ref: menuRef,
        }}
        onClick={stopPropagation}
        className="click-outside-ignore"
        sx={{ zIndex: zIndex.Tooltip + 1 }}
      >
        <MenuItem
          data-testid="show-all-columns"
          disabled={!hasHiddenCols}
          onClick={(event) => {
            event.stopPropagation();
            cols.forEach((column) => {
              platform.setRuntimeProp(
                appUid,
                pageId,
                column.itemId,
                'visible',
                true,
              );
              // updateRuntimeProps(column.itemId, { visible: true });
            });
          }}
        >
          <Icon className={classes.icon} iconName="check-double" />
          {t('Show All')}
        </MenuItem>
        <MenuItem
          data-testid="hide-all-columns"
          disabled={disableHideAll}
          onClick={(event) => {
            event.stopPropagation();
            let first = true;
            for (let i = 0; i < cols.length; i++) {
              const column = cols[i];
              if (
                column.type === 'RowNumberColumn' ||
                column.type === 'RowSelectionColumn'
              ) {
                continue;
              }
              if (first && column.visible) {
                first = false;
              } else {
                platform.setRuntimeProp(
                  appUid,
                  pageId,
                  column.itemId,
                  'visible',
                  false,
                );
              }
            }
          }}
        >
          <Icon className={classes.icon} iconName="eye-slash" />
          {t('Hide All')}
        </MenuItem>
        <Divider />
        {cols.map((column) => {
          const label = platform.evalIfExpressionString(
            appUid,
            pageId,
            column.itemId,
            'label',
            {},
            column.label,
          );
          if (column.type !== 'RowSelectionColumn' && label) {
            return (
              <MenuItem
                data-testid={`toggle-column-${column.itemId}`}
                disabled={totalVisible === 1 && column.visible}
                key={column.itemId}
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    column.visible &&
                    cols.filter((c) => c.visible).length === 1
                  ) {
                    return;
                  }
                  platform.setRuntimeProp(
                    appUid,
                    pageId,
                    column.itemId,
                    'visible',
                    !column.visible,
                  );
                }}
                aria-label={`${column.visible ? 'Hide' : 'Show'} ${
                  column.label
                }`}
              >
                <Icon
                  className={classes.icon}
                  iconName="check"
                  style={{
                    visibility: column.visible ? 'visible' : 'hidden',
                  }}
                />
                {label || t('Missing Label')}
              </MenuItem>
            );
          }
          return null;
        })}
      </Menu>
    </MenuItem>
  );
}
